<script lang="ts" setup>
import { ref, reactive, computed } from 'vue';
import { useAlerts } from '@/plugins/alerts';
import { useLocalization } from '@/plugins/localization';
import { Form } from '@/helpers/Form';
import DashboardsService, {
    WidgetModel,
    WidgetConfigModel,
} from '@/modules/core/dashboards/services/DashboardsService';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';
import ActiveSubstitutions from '@/modules/core/dashboards/components/widgets/active-substitutions/Widget.vue';
import AiUsage from '@/modules/core/dashboards/components/widgets/ai-usage/Widget.vue';

const emit = defineEmits<{
    (e: 'update', config: WidgetConfigModel, id: string): void;
    (e: 'add', config: WidgetConfigModel): void;
}>();

const props = defineProps<{
    dashboardId: number;
}>();

const { $alert } = useAlerts();
const { $t } = useLocalization();

const modal = ref<InstanceType<typeof IdeoModal> | null>(null);
const editMode = ref<boolean>(false);

const widget = reactive(
    Form.create<WidgetModel>({
        id: '',
        height: 0,
        width: 0,
        categories: [],
        description: '',
        icon: '',
        name: '',
        type: '',
    })
);

const config = reactive(
    Form.create<WidgetConfigModel>({
        id: '',
        height: 0,
        width: 0,
        x: 0,
        y: 0,
        sourceConfig: {},
    })
);

const submitBtn = computed(() =>
{
    return {
        variant: 'primary',
        text: editMode.value ? $t('[[[Zapisz widget]]]') : $t('[[[Dodaj widget]]]'),
    };
});

const component = computed(() =>
{
    switch (widget.type)
    {
        case 'table':
            return ActiveSubstitutions;
        case 'chart':
            return AiUsage;

        default: return null;
    }
});

const showModal = async (_widget: WidgetModel, mode: boolean = false) =>
{
    modal.value?.show();
    editMode.value = mode;

    await Promise.all([loadWidget(_widget.id), loadConfig(_widget.id)]);
};

const loadWidget = async (id: string) =>
{
    try
    {
        const response = await DashboardsService.getWidget(id);

        widget.withData(response);
    }
    catch (ex: any)
    {
        if (ex.code == 400) $alert.error(ex.message);
        else if (ex.code == 422) widget.$errors.record(ex.data.errors);
    }
};

const loadConfig = async (id: string) =>
{
    try
    {
        const response = await DashboardsService.getWidgetConfig(props.dashboardId, id);

        config.withData(response);
    }
    catch (ex: any)
    {
        if (ex.code == 400) $alert.error(ex.message);
        else if (ex.code == 422) config.$errors.record(ex.data.errors);
    }
};

const onSubmit = async () =>
{
    try
    {
        if (editMode.value)
        {
            await DashboardsService.updateWidgetConfig(props.dashboardId, config.id, config.data());

            emit('update', config.data(), config.id);
        }
        else
        {
            emit('add', config.data());
        }

        modal.value?.hide();
    }
    catch (ex: any)
    {
        if (ex.code == 400) $alert.error(ex.message);
        else if (ex.code == 422) config.$errors.record(ex.data.errors);
    }
};

const hidden = () =>
{
    widget.clear();
    config.clear();
};

defineExpose({
    showModal,
});
</script>

<template>
    <IdeoModal centered ref="modal" size="lg" :title="$t('[[[Ustawienia widgetu]]]')" @hidden="hidden">
        <template #default>
            <IdeoForm
                id="widget-settings-modal-form"
                @submit.prevent="onSubmit"
                @input="config.$errors.clear($event.target.name)"
            >
                <template v-if="component">
                    <component :is="component" :widget="widget" v-model:config="config" :properties-mode="true"></component>
                </template>
            </IdeoForm>
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton variant="light" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
            <IdeoButton
                type="submit"
                form="widget-settings-modal-form"
                :variant="submitBtn.variant"
                :disabled="!config.active()"
            >
                {{ submitBtn.text }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
